import { useContext } from 'react'
import { navigateToUrl } from 'single-spa'
import { clients, utils } from '@digital-commerce/shared-utils'
import { useCookie } from 'react-use'

import localeContext from '../../../contexts/Locale'
import config from '../../../config'
import createDigitalCommerceCartClient from '../../../clients/bff'
import { errorPageLoadDataLayerCreator } from '../../../utils/legacyAnalytics'
import { alloyEvents } from '../../../utils/alloyAnalytics'

const client = createDigitalCommerceCartClient(clients.bff)

const { getCurrentPath } = utils.cart

const ERROR_PAGE_SLUGS = {
  UNKNOWN_FAILURE: '/unknown-failure',
  SESSION_TIMEOUT: '/session-timeout',
  AUTH_EXPIRED: '/session-expired',
  EMPTY_CART: '/empty-cart'
}

const isJSRuntimeError = (error) => error?.name === 'TypeError'

const buildRedirectUrl = (lang, errorPageSlug) => {
  const { currentPath } = getCurrentPath(lang)
  return `/${currentPath}/notices${errorPageSlug}`
}

const isFIFAValidationError = (errorUrl) => errorUrl === '/cart-overview/validate'

const isSessionInvalid = (code, message) => {
  const isMessageInvalidSession = message === 'Session is required'

  return code === 404 && isMessageInvalidSession
}

const isAuthExpired = (code) => code === 403

const isCartNotFound = (url, code) => {
  return url === '/cart-overview' && code === 404
}

const onErrorUpdateLegacyDataLayer = (lang, prov, errorPagePath) => {
  // eslint-disable-next-line no-return-assign
  return (window.dataLayer = errorPageLoadDataLayerCreator(lang, prov, errorPagePath))
}

const getRedirectUrl = (error, lang, cartTypeCookie) => {
  // JS runtime error
  // This is usually a scenario where something is wrong with the integrity of
  // the cart data. We should make the customer see an error page and rebuild
  // their cart.
  if (isJSRuntimeError(error)) {
    return buildRedirectUrl(lang, ERROR_PAGE_SLUGS.SESSION_TIMEOUT)
  }

  const apiRequestErrorUrl = error?.config?.url
  const statusCode = error?.status
  const message = error?.data?.message

  if (isSessionInvalid(statusCode, message)) {
    return buildRedirectUrl(lang, ERROR_PAGE_SLUGS.SESSION_TIMEOUT)
  }

  // When auth token is expired.
  // Mobility carts should be redirected to the login page, while
  // HS or M&H carts should be redirected to the auth token expiry page
  if (isAuthExpired(statusCode)) {
    if (cartTypeCookie === 'MOBILITY') {
      const authProxyUrl = config.authorizationProxy.url
      return `${authProxyUrl}/login?rd=${window.location.href}`
    }
    return buildRedirectUrl(lang, ERROR_PAGE_SLUGS.AUTH_EXPIRED)
  }

  if (isCartNotFound(apiRequestErrorUrl, statusCode)) {
    return buildRedirectUrl(lang, ERROR_PAGE_SLUGS.EMPTY_CART)
  }

  if (isFIFAValidationError(apiRequestErrorUrl)) {
    return buildRedirectUrl(lang, ERROR_PAGE_SLUGS.UNKNOWN_FAILURE)
  }

  return buildRedirectUrl(lang, ERROR_PAGE_SLUGS.UNKNOWN_FAILURE)
}

const handleRedirect = (url) => {
  if (process.env.APP_ENV === 'local') return `Something is wrong: Page redirected to ${url}`
  navigateToUrl(url)
  return null
}

const log = async (error, redirectUrl) => {
  // Check for JS runtime error to prevent unwanted logging (ie. empty cart, expired session)
  if (isJSRuntimeError(error)) {
    try {
      await client.log({
        level: 'error',
        data: {
          message: 'Error caught in error boundary',
          meta: {
            redirectUrl,
            errorMessage: error.message
          }
        }
      })
    } catch (e) {
      console.error('Could not log error to GCP: ', e) // eslint-disable-line
    }
  }
}

/* eslint-disable react/prop-types */
const ErrorFallback = ({ error }) => {
  const [cartTypeCookie] = useCookie('cart_type')
  const { lang, prov } = useContext(localeContext)

  const handleError = () => {
    const redirectUrl = getRedirectUrl(error, lang, cartTypeCookie)
    // Step 1, Log error
    console.error(error) // eslint-disable-line
    log(error, redirectUrl)
    // Step 2, update analytics
    onErrorUpdateLegacyDataLayer(lang, prov, redirectUrl)
    alloyEvents.storageEvents.error({ error })
    // Step 3, redirect user to error page
    return handleRedirect(redirectUrl)
  }
  return handleError()
}

export default ErrorFallback
